import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileText, Clock, CheckCircle2, LogOut, Plus } from "lucide-react";
import SEO from "@/components/site/SEO";

const STATUS_STYLES = {
  New: "bg-[#F3D8DA] text-[#2A1C14]",
  Reviewing: "bg-[#E8DFD8] text-[#5A3B31]",
  "Missing Documents": "bg-amber-100 text-amber-900",
  Approved: "bg-emerald-100 text-emerald-900",
  Declined: "bg-rose-100 text-rose-900",
  Funded: "bg-[#2A1C14] text-[#F3D8DA]",
};

export default function Dashboard() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/applications/mine");
        setApps(data);
      } catch {
        toast.error("Failed to load your applications.");
      } finally { setLoading(false); }
    })();
  }, []);

  const onLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="max-w-5xl mx-auto px-6 py-16" data-testid="dashboard-page">
      <SEO title="Your Dashboard" description="Track your commission advance applications." path="/dashboard" />
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-10">
        <div>
          <span className="overline">Your Portal</span>
          <h1 className="font-serif-lux text-4xl sm:text-5xl mt-3">Welcome{user?.name ? `, ${user.name}` : ""}</h1>
          <p className="text-sm text-[#8C7A70] mt-1">{user?.email}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => nav("/apply")} className="btn-primary text-sm py-2.5 px-5" data-testid="dashboard-new-app">
            <Plus size={16} strokeWidth={1.5}/> New Application
          </button>
          <button onClick={onLogout} className="btn-ghost text-sm py-2.5 px-5" data-testid="dashboard-logout">
            <LogOut size={16} strokeWidth={1.5}/> Sign Out
          </button>
        </div>
      </div>

      <h2 className="font-serif-lux text-2xl mb-4">Your Applications</h2>
      {loading && <p className="text-[#8C7A70]">Loading...</p>}
      {!loading && apps.length === 0 && (
        <div className="lux-card p-14 text-center">
          <FileText className="w-12 h-12 mx-auto text-[#CBA4A4] mb-4" strokeWidth={1.5}/>
          <p className="font-serif-lux text-2xl">No applications yet</p>
          <p className="text-sm text-[#8C7A70] mt-2">Submit your first advance request to see it here.</p>
          <button onClick={() => nav("/apply")} className="btn-primary mt-6" data-testid="dashboard-first-app">Start Application</button>
        </div>
      )}
      {!loading && apps.length > 0 && (
        <div className="space-y-4">
          {apps.map((a) => (
            <div key={a.id} className="lux-card p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`dashboard-app-${a.id}`}>
              <div>
                <p className="font-serif-lux text-2xl text-[#2A1C14]">${Number(a.advance_amount_requested).toLocaleString()}</p>
                <p className="text-xs text-[#8C7A70] mt-1">Requested advance · Net commission ${Number(a.net_commission_amount).toLocaleString()}</p>
                <p className="text-xs text-[#5A3B31] mt-2">Property: {a.property_address}, {a.property_city} {a.property_state}</p>
                <p className="text-xs text-[#8C7A70] mt-1">Submitted {new Date(a.submitted_at).toLocaleDateString()} · Closing {a.expected_closing_date}</p>
              </div>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${STATUS_STYLES[a.status] || "bg-[#F4EEE8] text-[#2A1C14]"}`} data-testid={`dashboard-status-${a.id}`}>
                {a.status === "Funded" ? <CheckCircle2 size={14} strokeWidth={2}/> : <Clock size={14} strokeWidth={2}/>}
                {a.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
